import { useState } from "react";
import {
    Badge, Button, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import DispatchTransactionDialog from "../dialogs/DispatchTransactionDialog";
import {
    DISPATCHABLE_SUBJECTS, TRANSACTION_SUBJECT_LABELS, TRANSACTION_SUBJECT_COLORS,
    DEFAULT_CHIP_COLOR, TRANSACTIONS_RENDER_LIMIT,
} from "../groupsConstants";
import { SectionTitle, EmptyState } from "../groupsUi";

/**
 * Returns the chip color triplet for a transaction subject.
 * @param {string} subject
 */
function pickSubjectColor(subject) {
    return TRANSACTION_SUBJECT_COLORS[subject] || DEFAULT_CHIP_COLOR;
}

/**
 * Formats a transaction amount (signed, with currency suffix).
 * @param {Object} tx
 * @param {string} currency
 */
function formatAmount(tx, currency) {
    const sign = tx.increase ? "+" : "-";
    const amount = Number(tx.amount ?? 0).toFixed(2);
    return `${sign}${amount} ${currency}`.trim();
}

/**
 * Predicate: returns true if the transaction is eligible for dispatch.
 * @param {Object} tx
 */
function isDispatchable(tx) {
    return DISPATCHABLE_SUBJECTS.has(tx.subject) && !tx.dispatched && tx.increase;
}

/**
 * Subject chip with the original per-subject color.
 * @param {{subject:string}} props
 */
function SubjectBadge({ subject }) {
    const c = pickSubjectColor(subject);
    const label = TRANSACTION_SUBJECT_LABELS[subject] || subject;
    return (
        <Badge bg={c.bg} color={c.color} border="1px solid" borderColor={c.border}
            borderRadius="full" px="8px" py="2px" fontSize="0.68rem" fontWeight="600" textTransform="none">
            {label}
        </Badge>
    );
}

/**
 * Single transaction row.
 * @param {Object} props - { tx, currency, onDispatch }
 */
function TransactionRow({ tx, currency, onDispatch }) {
    return (
        <Tr>
            <Td>#{tx.id}</Td>
            <Td><SubjectBadge subject={tx.subject} /></Td>
            <Td color={tx.increase ? "rgba(129, 199, 132, 1)" : "rgba(229, 115, 115, 1)"} fontWeight="600">
                {formatAmount(tx, currency)}
            </Td>
            <Td>{tx.dispatched ? "Oui" : "Non"}</Td>
            <Td>{dayjs(tx.created_at).isValid() ? dayjs(tx.created_at).format("DD-MM-YYYY HH:mm") : "—"}</Td>
            <Td textAlign="right">
                {isDispatchable(tx) && (
                    <Button size="sm" colorScheme="brand" variant="outline" onClick={() => onDispatch(tx)}>Dispatch</Button>
                )}
            </Td>
        </Tr>
    );
}

/**
 * Limits the rendered list and reports if there are more entries.
 * @param {Array} list
 */
function buildVisible(list) {
    if (!Array.isArray(list)) return { visible: [], hidden: 0 };
    if (list.length <= TRANSACTIONS_RENDER_LIMIT) return { visible: list, hidden: 0 };
    return { visible: list.slice(0, TRANSACTIONS_RENDER_LIMIT), hidden: list.length - TRANSACTIONS_RENDER_LIMIT };
}

/**
 * Transactions table (header + visible rows).
 * @param {Object} props - { visible, currency, onDispatch }
 */
function TransactionsTable({ visible, currency, onDispatch }) {
    return (
        <TableContainer maxH="420px" overflowY="auto">
            <Table size="sm">
                <Thead>
                    <Tr><Th>ID</Th><Th>Sujet</Th><Th>Montant</Th><Th>Dispatched</Th><Th>Date</Th><Th textAlign="right">Action</Th></Tr>
                </Thead>
                <Tbody>
                    {visible.map((tx) => (<TransactionRow key={tx.id} tx={tx} currency={currency} onDispatch={onDispatch} />))}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

/**
 * Transactions tab listing wallet movements + dispatch trigger.
 * @param {Object} props - { detail, onRefresh }
 */
function TransactionsTab({ detail, onRefresh }) {
    const wallet = detail?.wallets?.[0];
    const currency = (wallet?.currency || "").toUpperCase();
    const transactions = detail?.transactions || [];
    const sorted = [...transactions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const { visible, hidden } = buildVisible(sorted);
    const [dispatchTx, setDispatchTx] = useState(null);
    const hiddenColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    if (transactions.length === 0) return <EmptyState>Aucune transaction.</EmptyState>;

    return (
        <Stack spacing="12px">
            <SectionTitle>Transactions ({transactions.length})</SectionTitle>
            <TransactionsTable visible={visible} currency={currency} onDispatch={setDispatchTx} />
            {hidden > 0 && (
                <Text color={hiddenColor} fontSize="0.75rem" textAlign="center">
                    {hidden} transaction{hidden > 1 ? "s" : ""} plus ancienne{hidden > 1 ? "s" : ""} non affichée{hidden > 1 ? "s" : ""}
                </Text>
            )}
            <DispatchTransactionDialog
                groupId={detail?.id}
                transaction={dispatchTx}
                members={detail?.members || []}
                currency={currency}
                onClose={() => setDispatchTx(null)}
                onDispatched={() => { setDispatchTx(null); onRefresh?.(); }}
            />
        </Stack>
    );
}

export default TransactionsTab;
